import { ChevronDown, ChevronRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { resolveNavigationIcon } from "../config/navigationIcons";
import { useNavGroupExpand } from "../hooks/useNavGroupExpand";
import Tooltip from "../../../_components/tooltip/Tooltip";
import "./MenuItem.scss";

const MenuItem = ({ item, mini, onClick }) => {
  const hasDdItems = item?.ddItems && item?.ddItems.length > 0;
  const location = useLocation();
  const { isExpanded, toggle } = useNavGroupExpand(item.menuItem);

  // Check if the item is active (either exact match or starts with)
  const isActive =
    location.pathname === item.path || location.pathname.startsWith(item.path);

  const IconComponent = resolveNavigationIcon(item.icon);
  const icon = IconComponent ? (
    <IconComponent size={16} strokeWidth={1.5} fill="none" stroke="currentColor" />
  ) : null;

  const MenuItemContent = (
    <>
      {hasDdItems ? (
        // Section header for items with dropdowns — label fades out when
        // collapsed but keeps its layout space, so children below don't jump.
        // Not clickable in mini mode: the chevron is hidden there and the
        // dropdown always renders open (see menu-section__dropdown below).
        <div
          className="menu-section__header"
          onClick={mini ? null : toggle}
          role={mini ? undefined : "button"}
          tabIndex={mini ? undefined : 0}
          onKeyDown={
            mini
              ? undefined
              : (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggle();
                  }
                }
          }
        >
          <p className={mini ? "menu-section__header-text--hidden" : "menu-section__header-text"}>
            {item.menuItem}
          </p>
          {!mini && (
            <span className="menu-section__chevron">
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
          )}
        </div>
      ) : (
        // Regular menu item
        <div
          className={`menu-item__content ${isActive ? "menu-item__content--active" : ""}`}
          onClick={mini ? onClick : null}
        >
          <div className="menu-item__left">
            <Tooltip content={item.menuItem} disabled={!mini} className="menu-item__icon-anchor">
              <div
                className={`menu-item__icon ${!item.icon ? "menu-item__icon--dot" : ""}`}
              >
                {icon}
              </div>
            </Tooltip>
            <div
              className={`menu-item__text-container ${mini ? "menu-item__text-container--hidden" : ""}`}
            >
              <p title={item.menuItem}>{item.menuItem}</p>
            </div>
            {item.meta && !mini && (
              <span className="menu-item__meta">{item.meta}</span>
            )}
          </div>
        </div>
      )}

      {hasDdItems && (mini || isExpanded) && (
        <div className="menu-section__dropdown">
          {item.ddItems.map((subItem) => (
            <MenuItem key={subItem.path} item={subItem} mini={mini} />
          ))}
        </div>
      )}
    </>
  );

  return hasDdItems ? (
    // For items with dropdowns, render as section header (no link)
    <div className="menu-section">{MenuItemContent}</div>
  ) : (
    // For regular items, render as clickable link
    <Link className="menu-item__anchor" to={item.path}>
      {MenuItemContent}
    </Link>
  );
};

export default MenuItem;
